import * as React from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Minus, Plus } from 'lucide-react'
import { formatDuration, MINUTE_IN_HOURS, parseDuration, roundToMinute } from '@/lib/inquiry-utils'

interface DurationInputProps {
  value: number // Duration in hours
  onChange: (value: number) => void
  disabled?: boolean
  min?: number // Minimum duration in hours
  step?: number // Step increment in hours
}

/**
 * DurationInput - Specialized input for time duration with +/- increment buttons
 *
 * Features:
 * - Displays duration as "X hours" or "X minutes"
 * - +/- buttons increment by the configured step (in hours)
 * - Text input for manual entry
 * - Parses natural language input (e.g., "2 hours", "30 minutes", "2.5h")
 * - Accepts any whole-minute duration; typed values snap to the nearest minute
 * - Enforces minimum value (defaults to 1 minute)
 */
export function DurationInput({
  value,
  onChange,
  disabled = false,
  min = MINUTE_IN_HOURS,
  step = 1
}: DurationInputProps) {
  const [displayValue, setDisplayValue] = React.useState(formatDuration(value))

  // Update display value when value changes externally (e.g., from buttons)
  React.useEffect(() => {
    setDisplayValue(formatDuration(value))
  }, [value])

  const handleIncrement = () => {
    const newValue = roundToMinute(value + step)
    onChange(newValue)
  }

  const handleDecrement = () => {
    const newValue = Math.max(min, roundToMinute(value - step))
    onChange(newValue)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const parsed = parseDuration(e.target.value)
    // Snap to whole minutes, then enforce minimum
    const clampedValue = Math.max(min, roundToMinute(parsed))
    onChange(clampedValue)
    setDisplayValue(formatDuration(clampedValue))
  }

  return (
    <div className="flex items-center w-full">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleDecrement}
        disabled={disabled || value <= min}
        className="h-9 w-9 rounded-none border-r border-input"
      >
        <Minus className="h-4 w-4" />
      </Button>
      <Input
        type="text"
        value={displayValue}
        onChange={(e) => setDisplayValue(e.target.value)}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder="30 minutes"
        className="flex-1 border-0 rounded-none text-center text-sm px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleIncrement}
        disabled={disabled}
        className="h-9 w-9 rounded-none border-l border-input"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  )
}
